<script setup>
import { computed, nextTick, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { META_EVENTS, META_EVENT_SECTIONS } from './metaConversion';

const props = defineProps({
  modelValue: { type: String, default: '' },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const open = ref(false);
const search = ref('');
const searchInput = ref(null);

const eventLabel = key => t(`AUTOMATION.FLOW.META.EVENTS.${key.toUpperCase()}`);
const selected = computed(() =>
  META_EVENTS.find(({ key }) => key === props.modelValue)
);

const sections = computed(() => {
  const term = search.value.trim().toLowerCase();
  return META_EVENT_SECTIONS.map(section => ({
    ...section,
    events: section.events.filter(
      ({ key }) =>
        !term ||
        key.toLowerCase().includes(term) ||
        eventLabel(key).toLowerCase().includes(term)
    ),
  })).filter(({ events }) => events.length);
});

const toggle = () => {
  open.value = !open.value;
  if (!open.value) return;
  search.value = '';
  nextTick(() => searchInput.value?.focus());
};

const select = key => {
  emit('update:modelValue', key);
  open.value = false;
};
</script>

<template>
  <div class="relative w-full min-w-0">
    <OnClickOutside @trigger="open = false">
      <button
        type="button"
        class="flex items-center w-full gap-2 !mb-0 !h-8 !px-2 !py-0 text-sm rounded-lg border bg-n-solid-1 text-n-slate-12 outline-none"
        :class="[
          hasError ? 'border-n-ruby-9' : 'border-n-weak',
          open && '!border-n-brand',
        ]"
        @click="toggle"
      >
        <Icon
          v-if="selected"
          :icon="selected.icon"
          class="size-4 shrink-0 text-n-slate-11"
        />
        <span
          class="flex-1 min-w-0 text-left truncate"
          :class="{ 'text-n-slate-10': !selected }"
        >
          {{
            selected
              ? eventLabel(selected.key)
              : t('AUTOMATION.FLOW.META.EVENT_PLACEHOLDER')
          }}
        </span>
        <span
          v-if="selected"
          class="font-mono text-xs truncate text-n-slate-11 shrink-0"
        >
          {{ selected.key }}
        </span>
        <Icon
          :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="size-4 shrink-0 text-n-slate-11"
        />
      </button>

      <div
        v-show="open"
        class="absolute z-50 mt-1 w-[340px] max-w-[90vw] border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
      >
        <div class="relative border-b border-n-strong">
          <span
            class="absolute i-lucide-search top-2.5 size-4 left-3 text-n-slate-11"
          />
          <input
            ref="searchInput"
            v-model="search"
            type="search"
            :placeholder="t('AUTOMATION.FLOW.META.SEARCH_EVENT')"
            class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
          />
        </div>
        <div class="py-1 overflow-y-auto max-h-80" role="listbox">
          <div v-for="section in sections" :key="section.key" class="pb-1">
            <div class="px-3 pt-2 pb-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t(`AUTOMATION.FLOW.META.SECTIONS.${section.key}.TITLE`) }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                {{
                  t(`AUTOMATION.FLOW.META.SECTIONS.${section.key}.DESCRIPTION`)
                }}
              </p>
            </div>
            <button
              v-for="event in section.events"
              :key="event.key"
              type="button"
              class="flex items-center w-full gap-2 !mb-0 px-3 py-2 text-sm text-left hover:bg-n-alpha-2"
              :class="{ 'bg-n-alpha-2': event.key === modelValue }"
              @click="select(event.key)"
            >
              <Icon
                :icon="event.icon"
                class="size-4 shrink-0 text-n-slate-11"
              />
              <span class="flex-1 min-w-0 truncate text-n-slate-12">
                {{ eventLabel(event.key) }}
              </span>
              <span class="font-mono text-xs text-n-slate-11 shrink-0">
                {{ event.key }}
              </span>
            </button>
          </div>
          <p
            v-if="!sections.length"
            class="px-3 py-2 mb-0 text-sm text-n-slate-11"
          >
            {{ t('COMBOBOX.EMPTY_STATE') }}
          </p>
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>
